import { useEffect, useRef, useState } from 'react';
import {
  Room,
  RoomEvent,
  Track,
  RemoteTrack,
  RemoteTrackPublication,
  RemoteParticipant,
} from 'livekit-client';

interface ProcessedStreamPlayerProps {
  livekitUrl: string;
  token: string;
  onError?: (msg: string) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

export default function ProcessedStreamPlayer({
  livekitUrl, token, onError, onConnected, onDisconnected,
}: ProcessedStreamPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const roomRef = useRef<Room | null>(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    if (!livekitUrl || !token) return;

    let mounted = true;
    const room = new Room({ adaptiveStream: true, dynacast: true });
    roomRef.current = room;

    const attachTrack = (track: RemoteTrack) => {
      if (track.kind === Track.Kind.Video && videoRef.current) {
        track.attach(videoRef.current);
      }
    };

    room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication, _participant: RemoteParticipant) => {
      if (!mounted) return;
      attachTrack(track);
    });

    room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
      track.detach();
    });

    room.on(RoomEvent.Disconnected, () => {
      if (!mounted) return;
      setConnected(false);
      onDisconnected?.();
    });

    // The publisher leaving is NOT `Disconnected` — that event is about our own
    // connection, which is still perfectly healthy. A backend publisher stands
    // down when its feed's compositor stops (livekit_rooms.StallWatch), and
    // without this the track is simply unsubscribed and detached: the caller
    // never hears about it, so `VideoRenderer` keeps the MJPEG <img> unmounted
    // and the tile goes black.
    //
    // Judged on whether any video is still being published, NOT on who left.
    // Every operator watching a feed is a participant in the same room, so a
    // second dashboard closing that tile also raises this event — and treating
    // that as a loss would drop a working tile back to MJPEG whenever a
    // colleague looked away.
    room.on(RoomEvent.ParticipantDisconnected, () => {
      if (!mounted) return;
      const stillPublishing = Array.from(room.remoteParticipants.values()).some(
        (p: RemoteParticipant) => Array.from(p.trackPublications.values()).some(
          (pub: RemoteTrackPublication) => pub.kind === Track.Kind.Video));
      if (stillPublishing) return;
      setConnected(false);
      onDisconnected?.();
    });

    const connect = async () => {
      setConnecting(true);
      try {
        await room.connect(livekitUrl, token);
        if (!mounted) return;
        setConnected(true);
        setConnecting(false);
        onConnected?.();

        room.remoteParticipants.forEach((participant: RemoteParticipant) => {
          participant.trackPublications.forEach((pub: RemoteTrackPublication) => {
            if (pub.track && pub.isSubscribed) {
              attachTrack(pub.track as RemoteTrack);
            }
          });
        });
      } catch (err: unknown) {
        if (!mounted) return;
        setConnecting(false);
        onError?.(err instanceof Error ? err.message : 'Failed to connect');
      }
    };

    connect();

    return () => {
      mounted = false;
      room.disconnect();
      roomRef.current = null;
      setConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livekitUrl, token]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', background: '#000' }}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="video-stream"
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />

      {connecting && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.7)', color: 'var(--color-accent)', fontSize: 14,
        }}>
          Connecting via WebRTC...
        </div>
      )}

      {connected && (
        <div style={{
          position: 'absolute', top: 8, right: 8,
          background: 'rgba(0,0,0,0.6)', padding: '2px 8px',
          borderRadius: 4, fontSize: 10, color: 'var(--color-accent)',
        }}>
          WebRTC
        </div>
      )}
    </div>
  );
}
